import clsx from "clsx";
import React from "react";
import { GenericComponentTypes } from "@/types/common/library";
import type { IconName } from "@fortawesome/fontawesome-common-types";

export enum ICON_SIZES {
  XXS = "2xs",
  XS = "xs",
  SM = "sm",
  BASE = "",
  LG = "lg",
  XL = "xl",
  XXL = "2xl",
}

enum ICON_ROTATE {
  BASE = "",
  DEG_90 = "rotate-90",
  DEG_180 = "rotate-180",
  DEG_270 = "rotate-270",
}

export enum ICON_FLIP {
  BASE = "",
  HORIZONTAL = "flip-horizontal",
  VERTICAL = "flip-vertical",
  BOTH = "flip-both",
}

export enum ICON_ANIMATE {
  BASE = "",
  BEAT = "beat",
  FADE = "fade",
  BEAT_FADE = "beat-fade",
  BOUNCE = "bounce",
  FLIP = "flip",
  SHAKE = "shake",
  SPIN = "spin",
}

export enum ICON_TYPE {
  CUSTOM = "fa-kit",
  LIGHT = "fa-light",
  REGULAR = "fa-regular",
  SOLID = "fa-solid",
  DUOTONE = "fa-duotone",
  BRAND = "fa-brands",
}

export type IconNameExtended =
  | `${IconName}`
  | `lula-${string}`
  | `custom-${string}`;

export type IconProps = {
  icon: IconNameExtended;
  type?: ICON_TYPE;
  title?: string;
  size?: ICON_SIZES | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  rotate?: ICON_ROTATE | number;
  flip?: ICON_FLIP;
  animate?: ICON_ANIMATE;
  onClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
  ref?: React.Ref<SVGSVGElement>;
} & GenericComponentTypes &
  Omit<
    React.SVGAttributes<SVGSVGElement>,
    "className" | "onClick" | "ref" | "title"
  >;

const ICON_NAME_PATTERN = /^[a-zA-Z0-9-]+$/;
const EXPLICIT_DIMENSION_CLASS_PATTERN = /(?:^|\s)(?:h-|size-|w-)/;

/** Sprite bundle folder under `public/sprites/` (e.g. bump when breaking sprite layout). */
const ICON_SPRITES_VERSION = "v3";
const ICON_SPRITE_BASE = `/sprites/${ICON_SPRITES_VERSION}`;

const ICON_SPRITE_PATHS: Record<ICON_TYPE, string> = {
  [ICON_TYPE.BRAND]: `${ICON_SPRITE_BASE}/brands.svg`,
  [ICON_TYPE.CUSTOM]: `${ICON_SPRITE_BASE}/custom-icons.svg`,
  [ICON_TYPE.DUOTONE]: `${ICON_SPRITE_BASE}/duotone-regular.svg`,
  [ICON_TYPE.LIGHT]: `${ICON_SPRITE_BASE}/light.svg`,
  [ICON_TYPE.REGULAR]: `${ICON_SPRITE_BASE}/regular.svg`,
  [ICON_TYPE.SOLID]: `${ICON_SPRITE_BASE}/solid.svg`,
};
const ICON_SIZE_EM: Record<Exclude<ICON_SIZES, ICON_SIZES.BASE>, string> = {
  [ICON_SIZES.XXS]: "0.625rem",
  [ICON_SIZES.XS]: "0.75rem",
  [ICON_SIZES.SM]: "0.875rem",
  [ICON_SIZES.LG]: "1.25rem",
  [ICON_SIZES.XL]: "1.5rem",
  [ICON_SIZES.XXL]: "2rem",
};
const ICON_ANIMATION_CLASSES: Record<
  Exclude<ICON_ANIMATE, ICON_ANIMATE.BASE>,
  string
> = {
  [ICON_ANIMATE.BEAT]: "animate-pulse",
  [ICON_ANIMATE.BEAT_FADE]: "animate-pulse",
  [ICON_ANIMATE.BOUNCE]: "animate-bounce",
  [ICON_ANIMATE.FADE]: "animate-pulse",
  [ICON_ANIMATE.FLIP]: "animate-icon-flip",
  [ICON_ANIMATE.SHAKE]: "animate-icon-shake",
  [ICON_ANIMATE.SPIN]: "animate-spin",
};

function sanitizeIconName(name: string): string {
  if (ICON_NAME_PATTERN.test(name)) return name;
  return name.replaceAll(/[^a-zA-Z0-9-]/g, "");
}

function getSizeValue(size?: IconProps["size"]): string {
  if (!size) return "0.875rem";
  if (Number.isInteger(size)) return `${size}rem`;
  return (
    ICON_SIZE_EM[size as Exclude<ICON_SIZES, ICON_SIZES.BASE>] ?? "0.875rem"
  );
}

function getFlipClasses(flip?: ICON_FLIP): string[] {
  if (flip === ICON_FLIP.HORIZONTAL) return ["-scale-x-100"];
  if (flip === ICON_FLIP.VERTICAL) return ["-scale-y-100"];
  if (flip === ICON_FLIP.BOTH) return ["-scale-x-100", "-scale-y-100"];
  return [];
}

function getFlipTransform(flip?: ICON_FLIP): string | null {
  if (flip === ICON_FLIP.HORIZONTAL) return "scaleX(-1)";
  if (flip === ICON_FLIP.VERTICAL) return "scaleY(-1)";
  if (flip === ICON_FLIP.BOTH) return "scale(-1, -1)";
  return null;
}

const Icon: React.FC<IconProps> = ({
  icon,
  type,
  className,
  size,
  rotate,
  flip,
  animate,
  onClick,
  ref: externalRef,
  style,
  ...other
}) => {
  const safeIcon = sanitizeIconName(
    typeof icon === "string" && icon.length > 0 ? icon : "question",
  );
  let iconType = type || ICON_TYPE.LIGHT;
  let sizeClass: string | number = size || "";
  let rotateClass: string | number = rotate || "";
  let rotateDeg: number | null = null;

  if (safeIcon.startsWith("custom") || safeIcon.startsWith("lula")) {
    iconType = ICON_TYPE.CUSTOM;
  }

  if (Number.isInteger(sizeClass)) {
    sizeClass = sizeClass + "x";
  }

  if (typeof rotate === "number" && Number.isFinite(rotate)) {
    rotateClass = "rotate-by";
    rotateDeg = rotate;
  }

  const callerClasses = clsx(className);
  const hasExplicitDimensions =
    EXPLICIT_DIMENSION_CLASS_PATTERN.test(callerClasses);
  const flipClasses = rotateDeg === null ? getFlipClasses(flip) : [];
  const animationClass = animate ? ICON_ANIMATION_CLASSES[animate] : undefined;
  const spritePath = ICON_SPRITE_PATHS[iconType];
  const href = `${spritePath}#${safeIcon}`;
  const classes = clsx(
    "inline-block fill-current align-middle shrink-0",
    iconType,
    `fa-${safeIcon}`,
    {
      [`fa-${sizeClass}`]: !!sizeClass,
      [`fa-${rotateClass}`]: !!rotateClass,
      [`fa-${flip}`]: !!flip,
      [`fa-${animate}`]: !!animate,
      [rotateClass]:
        typeof rotateClass === "string" && rotateDeg === null && !!rotateClass,
    },
    flipClasses,
    animationClass,
    callerClasses,
  );
  const transform =
    rotateDeg === null
      ? null
      : clsx(`rotate(${rotateDeg}deg)`, getFlipTransform(flip));
  const iconStyle =
    rotateDeg === null
      ? ({
          ...(!hasExplicitDimensions && {
            height: getSizeValue(size),
            width: getSizeValue(size),
          }),
          ...style,
        } as React.CSSProperties)
      : ({
          ...(!hasExplicitDimensions && {
            height: getSizeValue(size),
            width: getSizeValue(size),
          }),
          ...(transform && { transform, transformOrigin: "center" }),
          ...style,
          "--fa-rotate-angle": `${rotateDeg}deg`,
        } as React.CSSProperties);

  return (
    <svg
      ref={externalRef}
      aria-hidden="true"
      key={`icon-${iconType}-${safeIcon}`}
      className={classes}
      onClick={onClick}
      suppressHydrationWarning
      style={iconStyle}
      {...other}
    >
      <use href={href} />
    </svg>
  );
};
export { Icon };
export default Icon;
